/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import { useFormatDateByUserProfileLocation } from '@leagueplatform/web-common';
import { MasonryChartInterval } from '#types/masonry-chart.type.ts';
import { useGetDefaultTimezone } from '#hooks/use-get-default-timezone.hook.ts';
import { useThemeRawValues } from '@leagueplatform/genesis-core';
import * as BarGraphTypes from './types/bar-graph';
import { MasonryBarGraph } from './masonry-bar-graph';
import { formatMonthlyTick } from '../utils/use-monthly-ticks';
import {
  getIntervalXAxisTicks,
  getIntervalXAxisLabelTicks,
} from '../utils/get-interval-x-axis-ticks';

export type MasonryBarGraphProps<Datum> = Omit<
  BarGraphTypes.MasonryBarGraph<Datum>,
  | 'xAxisTickFormat'
  | 'barRadius'
  | 'xAxisTicks'
  | 'xAxisLabelTicks'
  | 'barPadding'
> & {
  interval: MasonryChartInterval;
};

export function MasonryBarGraphIntervalSelector<Datum>({
  interval,
  ...props
}: MasonryBarGraphProps<Datum>) {
  const defaultTimezone = useGetDefaultTimezone();
  const rawTheme = useThemeRawValues();
  const formatDateWithUserProfileLocation =
    useFormatDateByUserProfileLocation();

  const xAxisTicks = getIntervalXAxisTicks(
    props.data.map(props.accessors.xAccessor),
    interval,
    defaultTimezone,
  );
  const xAxisLabelTicks = getIntervalXAxisLabelTicks(
    props.data.map(props.accessors.xAccessor),
    interval,
    defaultTimezone,
  );

  if (interval === 'weekly') {
    return (
      <MasonryBarGraph
        xAxisTicks={xAxisTicks}
        xAxisLabelTicks={xAxisLabelTicks}
        xAxisTickFormat={(timestamp) =>
          formatDateWithUserProfileLocation(timestamp, {
            weekday: 'short',
          })
        }
        barRadius={rawTheme.radii.large}
        barPadding={0.3}
        {...props}
      />
    );
  }
  if (interval === 'monthly') {
    return (
      <MasonryBarGraph
        xAxisTicks={xAxisTicks}
        xAxisLabelTicks={xAxisLabelTicks}
        datumsPerColumn={1}
        xAxisTickFormat={formatMonthlyTick(
          formatDateWithUserProfileLocation,
          defaultTimezone,
        )}
        barRadius={rawTheme.radii.small}
        barPadding={0.3}
        {...props}
      />
    );
  }
  if (interval === 'yearly') {
    return (
      <MasonryBarGraph
        xAxisTicks={xAxisTicks}
        xAxisLabelTicks={xAxisLabelTicks}
        xAxisTickFormat={(timestamp) =>
          formatDateWithUserProfileLocation(timestamp, {
            month: 'narrow',
          })
        }
        barRadius={rawTheme.radii.large}
        barPadding={0.5}
        {...props}
      />
    );
  }
  if (interval === '5-yearly') {
    return (
      <MasonryBarGraph
        xAxisTicks={xAxisTicks}
        xAxisLabelTicks={xAxisLabelTicks}
        xAxisTickFormat={(timestamp) =>
          formatDateWithUserProfileLocation(timestamp, {
            year: 'numeric',
          })
        }
        barRadius={rawTheme.radii.large}
        barPadding={0.5}
        {...props}
      />
    );
  }
  return null;
}
